import React from 'react';
import styled from 'styled-components';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Columns, FileText, Image as ImageIcon, ZoomIn, ZoomOut } from 'lucide-react';
import { SearchFilterBar, type FilterPill } from '../shared/SearchFilterBar';
import type { DocumentReviewStatus } from '../../types';
import { ActionsMenu, type PageActionsConfig } from './ActionsMenu';
import type { ViewerMode } from './DocumentCanvasPanel';

// Status is DERIVED from confirming the title, date, and category — it is not
// directly settable. The tooltip explains what each derived state means.
const STATUS_TOOLTIPS: Record<DocumentReviewStatus, string> = {
    unreviewed: 'No fields confirmed yet. Confirm the title, date, and category to review this document.',
    'in-progress': 'Some fields confirmed. Confirm the title, date, and category to finish reviewing.',
    reviewed: 'Title, date, and category are all confirmed.',
    edited: 'Edited since last review.',
};

const STATUS_LABELS: Record<DocumentReviewStatus, string> = {
    unreviewed: 'Unreviewed',
    'in-progress': 'In progress',
    reviewed: 'Reviewed',
    edited: 'Needs attention',
};

export interface ToolbarPageNav {
    /** 1-based position of the current page within the visible set. */
    current: number;
    total: number;
    onPrevious: () => void;
    onNext: () => void;
}

/** Previous/next DOCUMENT navigation (moved out of the page rail). Either
 *  side may be absent at the ends of the case. */
export interface ToolbarDocNav {
    previousTitle?: string | null;
    nextTitle?: string | null;
    onPrevious?: () => void;
    onNext?: () => void;
}

export interface ToolbarZoom {
    /** 1.0 = base width. */
    value: number;
    min: number;
    max: number;
    onZoomIn: () => void;
    onZoomOut: () => void;
    onReset: () => void;
}

export interface DocumentInspectToolbarProps {
    /** Search input — shared with the case-level search via App.tsx state. */
    searchValue: string;
    onSearchChange: (value: string) => void;
    searchPills?: FilterPill[];
    onRemoveSearchPill?: (id: string) => void;

    /** DERIVED review status (read-only) — shown as a badge, not settable. */
    status: DocumentReviewStatus;

    /** Page-level Actions menu. */
    pageActions?: PageActionsConfig;

    /** Image / Text / Side-by-Side toggle. */
    viewerMode: ViewerMode;
    onViewerModeChange: (mode: ViewerMode) => void;

    /** Right-aligned page navigation (prev / "N / M" / next). */
    pageNav?: ToolbarPageNav;
    /** Right-aligned previous/next document navigation. */
    docNav?: ToolbarDocNav;
    /** Right-aligned zoom cluster (absorbs the old floating canvas control). */
    zoom?: ToolbarZoom;
}

const Bar = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
`;

const SearchSlot = styled.div`
    flex: 1 1 auto;
    min-width: 140px;
    max-width: 220px;
`;

const Spacer = styled.div`
    flex: 1 1 auto;
`;

const RightGroup = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
`;

// Read-only derived-status badge. A green dot for "reviewed", neutral otherwise —
// mirrors the StatusDot used in the document table.
const StatusBadge = styled.span<{ $status: DocumentReviewStatus }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    border-radius: 6px;
    border: 1px solid #e0e0e0;
    background: #fafafa;
    color: ${({ $status }) => ($status === 'reviewed' ? '#1a7a4c' : '#666')};
    font-size: 13px;
    font-weight: 500;
    white-space: nowrap;

    &::before {
        content: '';
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: ${({ $status }) => ($status === 'reviewed' ? '#1a7a4c' : '#bbb')};
    }
`;

const ModeToggle = styled.div`
    display: inline-flex;
    border-radius: 6px;
    overflow: hidden;
    border: 1px solid #e0e0e0;
`;

/* Icon-only segments (intentional component change): the original label text
   survives as tooltip + aria-label. */
const ModeButton = styled.button<{ $active: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 6px 10px;
    border: none;
    cursor: pointer;
    color: ${({ $active }) => ($active ? '#fff' : '#666')};
    background: ${({ $active }) => ($active ? '#111' : '#fff')};
`;

const ModeConfigs: Array<{ mode: ViewerMode; label: string; icon: React.ReactNode }> = [
    { mode: 'image', label: 'Image', icon: <ImageIcon size={16} /> },
    { mode: 'ocr', label: 'Text', icon: <FileText size={16} /> },
    { mode: 'split', label: 'Side by Side', icon: <Columns size={16} /> },
];

/* ── Right cluster: page nav + zoom ────────────────────────── */

const NavCluster = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: #666;
    white-space: nowrap;
`;

const NavIconButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border: none;
    border-radius: 4px;
    background: transparent;
    color: #555;
    cursor: pointer;

    &:hover:not(:disabled) { background: #f0f0f0; }
    &:disabled { opacity: 0.35; cursor: default; }
`;

const PageIndicator = styled.span`
    font-variant-numeric: tabular-nums;

    strong {
        font-weight: 600;
        color: #111;
    }
`;

const ZoomCluster = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding-left: 8px;
    border-left: 1px solid #e0e0e0;
`;

const ZoomLevelButton = styled.button`
    min-width: 44px;
    height: 24px;
    padding: 0 4px;
    border: none;
    border-radius: 4px;
    background: transparent;
    color: #555;
    font-size: 12px;
    font-variant-numeric: tabular-nums;
    cursor: pointer;
    font-family: inherit;

    &:hover { background: #f0f0f0; }
`;

export const DocumentInspectToolbar: React.FC<DocumentInspectToolbarProps> = ({
    searchValue,
    onSearchChange,
    searchPills,
    onRemoveSearchPill,
    status,
    pageActions,
    viewerMode,
    onViewerModeChange,
    pageNav,
    docNav,
    zoom,
}) => (
    <Bar>
        <SearchSlot>
            <SearchFilterBar
                searchText={searchValue}
                onSearchChange={onSearchChange}
                placeholder="Search page number or text…"
                compact
                pills={searchPills}
                onRemovePill={onRemoveSearchPill}
            />
        </SearchSlot>
        <StatusBadge $status={status} title={STATUS_TOOLTIPS[status]}>
            {STATUS_LABELS[status]}
        </StatusBadge>
        <ActionsMenu config={pageActions} />
        <ModeToggle role="group" aria-label="Toggle viewer mode">
            {ModeConfigs.map(config => (
                <ModeButton
                    key={config.mode}
                    type="button"
                    $active={viewerMode === config.mode}
                    onClick={() => onViewerModeChange(config.mode)}
                    aria-pressed={viewerMode === config.mode}
                    aria-label={config.label}
                    title={config.label}
                >
                    {config.icon}
                </ModeButton>
            ))}
        </ModeToggle>
        <Spacer />
        <RightGroup>
            {docNav && (docNav.onPrevious || docNav.onNext) && (
                <NavCluster role="group" aria-label="Document navigation">
                    <NavIconButton
                        type="button"
                        onClick={docNav.onPrevious}
                        disabled={!docNav.onPrevious}
                        aria-label="Previous document"
                        title={docNav.previousTitle
                            ? `Previous document: ${docNav.previousTitle}`
                            : 'No previous document'}
                    >
                        <ChevronLeft size={15} />
                    </NavIconButton>
                    <span>Document</span>
                    <NavIconButton
                        type="button"
                        onClick={docNav.onNext}
                        disabled={!docNav.onNext}
                        aria-label="Next document"
                        title={docNav.nextTitle
                            ? `Next document: ${docNav.nextTitle}`
                            : 'No next document'}
                    >
                        <ChevronRight size={15} />
                    </NavIconButton>
                </NavCluster>
            )}
            {pageNav && pageNav.total > 0 && (
                <NavCluster>
                    <NavIconButton
                        type="button"
                        onClick={pageNav.onPrevious}
                        disabled={pageNav.current <= 1}
                        aria-label="Previous page"
                        title="Previous page (↑)"
                    >
                        <ChevronUp size={15} />
                    </NavIconButton>
                    <PageIndicator aria-label={`Page ${pageNav.current} of ${pageNav.total}`}>
                        <strong>{pageNav.current}</strong> / {pageNav.total}
                    </PageIndicator>
                    <NavIconButton
                        type="button"
                        onClick={pageNav.onNext}
                        disabled={pageNav.current >= pageNav.total}
                        aria-label="Next page"
                        title="Next page (↓)"
                    >
                        <ChevronDown size={15} />
                    </NavIconButton>
                </NavCluster>
            )}
            {zoom && (
                <ZoomCluster role="group" aria-label="Zoom page image">
                    <NavIconButton
                        type="button"
                        onClick={zoom.onZoomOut}
                        disabled={zoom.value <= zoom.min}
                        aria-label="Zoom out"
                        title="Zoom out"
                    >
                        <ZoomOut size={15} />
                    </NavIconButton>
                    <ZoomLevelButton
                        type="button"
                        onClick={zoom.onReset}
                        aria-label="Reset zoom"
                        title="Reset zoom"
                    >
                        {Math.round(zoom.value * 100)}%
                    </ZoomLevelButton>
                    <NavIconButton
                        type="button"
                        onClick={zoom.onZoomIn}
                        disabled={zoom.value >= zoom.max}
                        aria-label="Zoom in"
                        title="Zoom in"
                    >
                        <ZoomIn size={15} />
                    </NavIconButton>
                </ZoomCluster>
            )}
        </RightGroup>
    </Bar>
);
